import { RemoteApiFetchErrorFactory } from './RemoteApiError'
import CoreServicesRemoteApi from './CoreServicesRemoteApi';
import UIApiRemoteApi from './UIRemoteApi';
import QueryParameters from './QueryParameters';
import APIManagerRemoteApi from './APIManagerRemoteApi';
import { GetState } from '../types';
import { default as ARMRemoteApi } from './ARMRemoteApi';
import APIPlatformRemoteApi from './APIPlatformRemoteApi';
import XAPIProxy from './XAPIProxy';
import ArchiveAPIService from "./ArchiveAPIService";
const DEFAULT_CACHE_TTL_MS = 30_000
const requestCache = new Map<string, { promise: Promise<any>; timestamp: number }>()

const POST = 'POST';
const PATCH = 'PATCH';
const PUT = 'PUT';
const GET = 'GET';
const DELETE = 'DELETE';

export type HttpMethod = 'POST' | 'PUT' | 'GET' | 'DELETE' | 'PATCH'
export const SEPARATOR = '/';

type Body = { [key: string]: any } | FormData | string | any[] | any
export type StringMap = { [key: string]: string | number | boolean }

export type Response = {
  status: number,
  headers: Headers,
  body: Body,
  json(): Promise<any>,
  text(): Promise<string>
}

export type Pagination = {
  limit: number
  offset: number
}

class BaseRemoteApi {
  authorization: string;
  baseUrl: string;

  static _isError(response: { status: number }) {
    return response.status < 200 || response.status >= 300
  }

  private static getResponse(response: any, jsonResult?: boolean): Promise<any> | Promise<string> {
    if (jsonResult === false) {
      return response.text()
    } else {
      return response.json()
    }
  }

  constructor(baseUrl: string, authorization: string) {
    this.baseUrl = baseUrl;
    this.authorization = authorization
  }

  _get(pathElements: string[], jsonResult?: boolean, headers?: StringMap, queryParameters?: StringMap, getHeaders?: boolean): Promise<any> {
    return this._request(GET, pathElements, undefined, jsonResult, headers, queryParameters, getHeaders)
  }

  _getWithURL(url: string, jsonResult?: boolean, headers?: StringMap, queryParameters?: StringMap, getHeaders?: boolean): Promise<any> {
    const pathElements = url.split('/').splice(3)
    const lastPathElement = pathElements.pop()
    const splitLastPathElement = lastPathElement ? lastPathElement.split('?') : undefined
    const queryParams = queryParameters ? queryParameters : {}

    if (splitLastPathElement) {
      pathElements.push(splitLastPathElement[0])
      const splitQueryParams = splitLastPathElement[1].split('&')

      splitQueryParams.forEach(splitQueryParam => {
        const valuesQueryParam = splitQueryParam.split('=')
        queryParams[valuesQueryParam[0]] = valuesQueryParam[1]
      })
    }

    return this._request(GET, pathElements, undefined, jsonResult, headers, queryParams, getHeaders)
  }

  _delete(pathElements: string[], body?: Body, jsonResult?: boolean): Promise<any> {
    return this._request(DELETE, pathElements, body, jsonResult)
  }

  _post(pathElements: string[], body: Body, jsonResult?: boolean): Promise<any> {
    return this._request(POST, pathElements, body, jsonResult)
  }

  _patch(pathElements: string[], body: Body, jsonResult?: boolean, headers?: StringMap): Promise<any> {
    return this._request(PATCH, pathElements, body, jsonResult, headers)
  }

  _put(pathElements: string[], body: Body, jsonResult?: boolean, headers?: StringMap, queryParameters?: StringMap): Promise<any> {
    return this._request(PUT, pathElements, body, jsonResult, headers, queryParameters)
  }

  _request(method: HttpMethod, pathElements: string[], body?: Body, jsonResult?: boolean, optionalHeader?: StringMap,
           queryParameters?: StringMap, getHeaders?: boolean): Promise<any> {
    const url = `${this._url(pathElements)}?${QueryParameters.buildParameters(queryParameters)}`
    const defaultHeaders = body ? this._headers() : this._authorizationHeader();
    const newBody: string | Buffer | FormData = (
      body instanceof Buffer ||
      typeof body === 'string' ? body : JSON.stringify(body));

    const headers = {
      ...defaultHeaders,
      ...optionalHeader
    };

    const options: RequestInit = {
      method: method,
      headers: headers,
      body: newBody,
      credentials: 'same-origin'
    };

    return fetch(url, options)
      .then((response: any) => {
          if (BaseRemoteApi._isError(response)) {
            const errorOptions: RequestOptions = {...options, json: jsonResult, uri: url} as RequestOptions;
            return RemoteApiFetchErrorFactory.fromResponseAndOptions(response, errorOptions).then(
              c => {
                throw c
              })
          } else {
            if (getHeaders) {
              return {headers: response.headers, dataPromise: BaseRemoteApi.getResponse(response, jsonResult)}
            } else {
              return BaseRemoteApi.getResponse(response, jsonResult)
            }
          }
        }
      ).catch((err: { type?: string, message?: string }) => {
        if (!err.type) {
          const errorOptions: RequestOptions = {...options, json: jsonResult, uri: url} as RequestOptions;
          throw RemoteApiFetchErrorFactory.connectionLostError(err, errorOptions, err.message)
        } else {
          throw err
        }
      })
  }

  _url(elements: string[]): string {
    return this.baseUrl + SEPARATOR + elements.join(SEPARATOR)
  }

  _authorizationHeader(): StringMap {
    const auth = this.authorization;
    const authorization = !auth ? '' : (auth.indexOf('Bearer ') === -1 ? 'Bearer ' : '') + auth;
    return {
      'Authorization': authorization
    }
  }

  _headers(): StringMap {
    return {
      ...this._authorizationHeader(),
      'content-type': 'application/json'
    }
  }

  _cachedGet(cacheKey: string, pathElements: string[], jsonResult?: boolean, headers?: StringMap, queryParameters?: StringMap, ttlMs: number = DEFAULT_CACHE_TTL_MS): Promise<any> {
    const cached = requestCache.get(cacheKey)
    if (cached && Date.now() - cached.timestamp < ttlMs) {
      return cached.promise
    }
    const promise = this._get(pathElements, jsonResult, headers, queryParameters)
    requestCache.set(cacheKey, { promise, timestamp: Date.now() })
    promise.catch(() => requestCache.delete(cacheKey))
    return promise
  }

  initialCache(prop: string): Promise<any> | undefined {
    const fn = window.mc && window.mc[prop];
    if (!fn) return undefined;
    window.mc[prop] = undefined; // only use once
    const result = typeof fn === 'object' ? fn : fn(this._headers().Authorization)

    return Promise.resolve(result);
  }
}

export type RequestOptions = {
  method: string,
  uri: string,
  json?: boolean,
  body?: Body | {},
  headers: StringMap
}

export type ThunkAPI = {
  cs: (getState: GetState) => CoreServicesRemoteApi
  ui: (getState: GetState) => UIApiRemoteApi
  arm: (getState: GetState) => ARMRemoteApi
  apiManager: (getState: GetState) => APIManagerRemoteApi
  xapiProxy: (getState: GetState) => XAPIProxy
  apiPlatform: (getState: GetState) => APIPlatformRemoteApi
  archiveAPI: (getState: GetState) => ArchiveAPIService
}

export default BaseRemoteApi
